import * as React from "react"
import type { FormSpec } from "./formSpec"
import { fieldErrorsToState } from "./formValidation"
import { validateField } from "./validation"

type ValidationEvent = "onChange" | "onBlur" | "onSubmit"

type Params = {
    spec: FormSpec
    values: Record<string, unknown>
    setErrors: React.Dispatch<React.SetStateAction<Record<string, { message: string }>>>
    didSubmitRef: React.MutableRefObject<boolean>
}

type Api = {
    shouldValidate: (event: ValidationEvent) => boolean
    validateField: (name: string) => Promise<0 | 1>
    validateFieldWithValues: (name: string, nextValues: Record<string, unknown>) => Promise<0 | 1>
    validateAll: () => Promise<boolean>
    validateAllWithValues: (nextValues: Record<string, unknown>) => Promise<boolean>
}

export function useValidation({ spec, values, setErrors, didSubmitRef }: Params): Api {
    const shouldValidate = React.useCallback(
        (event: ValidationEvent): boolean => {
            const mode = spec.form?.mode || "all"
            const reMode = spec.form?.reValidateMode || "onChange"
            if (mode === "all") return true
            if (mode === event) return true
            if (didSubmitRef.current && reMode === event) return true
            return false
        },
        [spec, didSubmitRef]
    )

    const validateFieldWithValues = React.useCallback(
        async (name: string, nextValues: Record<string, unknown>): Promise<0 | 1> => {
            const def = (spec.fields as any)?.[name]
            if (!def) return 1
            if (!shouldValidate("onBlur") && !shouldValidate("onSubmit")) return 1

            const msg = await validateField(def, nextValues)

            if (!msg) {
                setErrors((prev) => {
                    if (!prev[name]) return prev
                    const copy = { ...prev }
                    delete copy[name]
                    return copy
                })
                return 1
            }

            setErrors((prev) => ({ ...prev, [name]: { message: msg } }))
            return 0
        },
        [spec, setErrors, shouldValidate]
    )

    const validateFieldApi = React.useCallback(
        async (name: string): Promise<0 | 1> => {
            return validateFieldWithValues(name, values)
        },
        [validateFieldWithValues, values]
    )

    const validateAllWithValues = React.useCallback(
        async (nextValues: Record<string, unknown>): Promise<boolean> => {
            const fe: Record<string, string> = {}

            for (const name of Object.keys(spec.fields || {})) {
                const def = (spec.fields as any)[name]
                const msg = await validateField(def, nextValues)
                if (msg) fe[name] = msg
            }

            if (Object.keys(fe).length === 0) {
                setErrors({})
                return true
            }

            setErrors(fieldErrorsToState(fe))
            return false
        },
        [spec, setErrors]
    )

    const validateAll = React.useCallback(async (): Promise<boolean> => {
        return validateAllWithValues(values)
    }, [validateAllWithValues, values])

    return {
        shouldValidate,
        validateField: validateFieldApi,
        validateFieldWithValues,
        validateAll,
        validateAllWithValues,
    }
}
